import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Wand2, Copy, CheckCircle2, Loader2, Eye, ExternalLink } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SnapshotCreateDialog — captures the current Stack Map view as a public read-only snapshot.
 * Sends the frozen `stack_data` (nodes/links) so future data changes never leak into the shared view.
 */
export default function SnapshotCreateDialog({
  open, onOpenChange, token,
  // Current view state (all frozen at creation time)
  stackData, selectedProperty, propertyName, selectedNode, layoutMode, activeLayer, focusedCategory, filters,
}) {
  const [name, setName] = useState('');
  const [expiresDays, setExpiresDays] = useState('30');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { slug, url, expires_at }

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const reset = () => { setName(''); setExpiresDays('30'); setResult(null); setSaving(false); };
  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async () => {
    if (!stackData || !stackData.nodes || stackData.nodes.length === 0) {
      toast.error('Nothing to snapshot — the stack map is empty.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim() || `${propertyName || 'Stack'} snapshot`,
        property_id: selectedProperty || 'overall',
        property_name: propertyName || '',
        node_id: selectedNode?.id || null,
        node_name: selectedNode?.name || '',
        layout_mode: layoutMode || 'category',
        layer: activeLayer || 'actual',
        focused_category: focusedCategory || null,
        filters: filters || {},
        expires_days: parseInt(expiresDays, 10) || 0,
        stack_data: stackData,
      };
      const r = await axios.post(`${BACKEND}/api/admin/snapshots`, body, { headers });
      const fullUrl = `${window.location.origin}${r.data.url}`;
      setResult({ ...r.data, fullUrl });
      // Auto-copy
      try { await navigator.clipboard.writeText(fullUrl); } catch { /* empty */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create snapshot');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!result?.fullUrl) return;
    try {
      await navigator.clipboard.writeText(result.fullUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — grab it manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="snapshot-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wand2 className="h-4 w-4 text-indigo-500" /> Create Snapshot Link
          </DialogTitle>
          <DialogDescription className="text-xs">
            Freeze the current Stack Map view and get a read-only public URL. Anyone with the link can view without logging in. Data is captured as-is now — future changes won&apos;t affect the snapshot.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="py-3 space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 text-xs space-y-1">
              <div><span className="text-muted-foreground">Property:</span> <span className="font-medium">{propertyName || (selectedProperty === 'overall' ? 'Overall Map' : selectedProperty)}</span></div>
              {selectedNode && <div><span className="text-muted-foreground">Focused node:</span> <span className="font-medium">{selectedNode.name}</span></div>}
              <div><span className="text-muted-foreground">Layout:</span> <span className="font-medium">{layoutMode || 'category'}</span></div>
              <div><span className="text-muted-foreground">Nodes captured:</span> <span className="font-medium">{stackData?.nodes?.length || 0}</span></div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="snap-name" className="text-xs">Snapshot name (optional)</Label>
              <Input
                id="snap-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={`${propertyName || 'Stack'} snapshot`}
                className="h-9 text-sm"
                data-testid="snapshot-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Expires after</Label>
              <Select value={expiresDays} onValueChange={setExpiresDays}>
                <SelectTrigger className="h-9 text-sm" data-testid="snapshot-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days (recommended)</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="0">Never (permanent)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Eye className="h-3 w-3" /> View counts and expiry are visible to anyone with the link.
            </p>
          </div>
        ) : (
          <div className="py-3 space-y-4" data-testid="snapshot-result">
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold text-emerald-900 dark:text-emerald-300">Snapshot created — link copied!</div>
                <div className="text-[10px] text-muted-foreground">Anyone with this URL can view the frozen map.</div>
              </div>
            </div>
            <div className="flex items-center gap-1 rounded-md border bg-background px-2 py-1.5">
              <input
                readOnly
                value={result.fullUrl}
                className="flex-1 bg-transparent outline-none text-xs font-mono truncate"
                data-testid="snapshot-url"
                onClick={e => e.currentTarget.select()}
              />
              <Button size="sm" variant="ghost" className="h-7" onClick={copyLink} data-testid="snapshot-copy-btn">
                <Copy className="h-3 w-3 mr-1" /> Copy
              </Button>
              <a href={result.fullUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-7 px-2 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="snapshot-open-link">
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            {result.expires_at && (
              <p className="text-[10px] text-muted-foreground">Expires: {new Date(result.expires_at).toLocaleString()}</p>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)} disabled={saving}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="snapshot-submit">
                {saving && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                Create Snapshot
              </Button>
            </>
          ) : (
            <Button onClick={() => handleClose(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
